// Auto-generated from docs/architecture/flux-ev.md — do not edit manually
const content = `
# Flux EV Architecture

Electric vehicle fleet charging optimization domain. Provides standalone LP-based
charging optimization and participates as an agent in ADMM multi-agent coordination
for site-level co-optimization with battery storage and cold storage.

## Overview

Flux EV solves the EV fleet charging scheduling problem: given a fleet of vehicles
with known arrival/departure windows, battery specs, and daily driving distances,
find the minimum-cost charging schedule that respects charger infrastructure limits,
vehicle presence windows, and optional site power constraints.

Two project types are supported:

- **Fleet projects** -- Define vehicles, chargers, and tariff; run multi-strategy
  MPC simulation (opportunity / demand-limited / cost-optimized) to compare annual
  charging costs. This is the primary workflow.
- **Depot projects** -- Upload an observed aggregate depot power curve (CSV), decompose
  it into individual charging sessions via MIP-based fitting, then generate an
  annual load profile from the decomposition.

Two operational modes:

- **Standalone mode** -- The EV optimizer runs in isolation, minimizing its own
  energy + demand cost. Output is an 8760-hour (or 35,040 at 15-min) load curve
  with FlexibilityWindows describing what load can shift.
- **Coordinated (ADMM) mode** -- \`EVFleetAgent\` wraps the LP optimizer and participates
  in the orchestrator's multi-agent simulation. The agent receives coordination
  signals (congestion prices, power envelopes, ADMM consensus targets) and re-solves
  every 15-minute timestep with actual per-vehicle SOC state (true MPC). A piecewise-
  linear (PWL) approximation of the quadratic ADMM penalty is injected into the LP
  objective because HiGHS IPM hangs on the EV problem structure with a QP Hessian.

## Architecture

\`\`\`
Frontend (flux-ev-web)
    |
    | REACT_APP_API_URL
    v
Platform API (vortx-platform-api)   -- auth + routing
    |
    | X-User-ID / X-User-Email headers
    v
Flux EV API (flux-ev-api)           -- Flask on Cloud Run
    |
    +-- ev_optimizer.py              -- HiGHS LP solver
    +-- ev_mpc_controller.py         -- MPC receding horizon
    +-- fleet_core_adapter.py        -- Forward simulation (opportunity/demand-managed)
    +-- depot_forward_simulator.py   -- MIP session fitting
    +-- depot_session_optimizer.py   -- Simulated annealing session fitting
    +-- charging_curve.py            -- CC-CV charging curve interpolation
    |
    v
Firestore (vortx-flux-site-dna)
    users/{uid}/projects/{pid}
        apps.flux_ev.config: { fleet, chargers, site_config, ... }
    users/{uid}/projects/{pid}/ev_scenarios/{sid}
        chargers, tariff_id, strategy, results
    users/{uid}/projects/{pid}/simulation_results/{rid}
\`\`\`

For coordinated mode, \`EVFleetAgent\` lives in \`libs/python/flux-core\` and is
imported by the orchestrator/EMS service. It lazy-imports \`ev_optimizer.py\` from
\`apps/flux-ev-api/services/\` via \`importlib.util.spec_from_file_location\` to avoid
package name collisions.

## API Routes

All routes require authentication via the Platform API (Firebase ID token).
User context is forwarded in \`X-User-ID\` and \`X-User-Email\` headers.

### Project Routes (\`/api/v1/ev/projects\`)

| Method | Path | Handler | Description |
|--------|------|---------|-------------|
| POST | \`/\` | \`create_project()\` | Create new EV project (fleet or depot type) |
| GET | \`/\` | \`list_projects()\` | List user's EV projects |
| GET | \`/with-scenarios\` | \`list_projects_with_scenarios()\` | List projects with scenario summaries and available load strategies |
| GET | \`/<project_id>\` | \`get_project()\` | Get a specific project |
| PUT | \`/<project_id>\` | \`update_project()\` | Update project fields |
| DELETE | \`/<project_id>\` | \`delete_project()\` | Delete project and its scenarios |
| POST | \`/<project_id>/simulate\` | \`run_simulation()\` | Run fleet-core simulation (legacy, single-strategy) |
| GET | \`/<project_id>/scenarios/<sid>/load\` | \`get_scenario_load()\` | Get \`loads_kw\` for a specific scenario + strategy |
| GET | \`/vehicles\` | \`list_vehicles()\` | List vehicle database entries |
| GET | \`/vehicles/<vehicle_id>\` | \`get_vehicle()\` | Get a specific vehicle spec |
| POST | \`/<project_id>/export\` | \`export_to_site_dna()\` | Export EV load to Site DNA project (new_project or add_scenario mode) |

### Scenario Routes (\`/api/v1/ev/projects/<project_id>/scenarios\`)

| Method | Path | Handler | Description |
|--------|------|---------|-------------|
| POST | \`/\` | \`create_scenario()\` | Create scenario (chargers, tariff, strategy) |
| GET | \`/\` | \`list_scenarios()\` | List all scenarios for a project |
| GET | \`/<scenario_id>\` | \`get_scenario()\` | Get a specific scenario |
| PUT | \`/<scenario_id>\` | \`update_scenario()\` | Update scenario fields |
| DELETE | \`/<scenario_id>\` | \`delete_scenario()\` | Delete a scenario |
| POST | \`/<scenario_id>/duplicate\` | \`duplicate_scenario()\` | Duplicate scenario |
| POST | \`/<scenario_id>/simulate\` | \`simulate_scenario()\` | Run 3-strategy MPC simulation (main workflow) |
| POST | \`/simulate-batch\` | \`simulate_batch()\` | Batch simulate with SSE streaming progress |

### Optimizer Routes (\`/api/v1/ev/optimize\`)

| Method | Path | Handler | Description |
|--------|------|---------|-------------|
| POST | \`/projects/<project_id>/optimize\` | \`optimize_project()\` | Standalone LP optimization for a project |
| POST | \`/projects/<project_id>/negotiate\` | \`negotiate_schedule()\` | Re-optimize with site power limits (multi-agent negotiation) |
| POST | \`/optimize\` | \`quick_optimize()\` | Quick optimization without a saved project |

### Depot Routes (\`/api/v1/ev/depot\`)

| Method | Path | Handler | Description |
|--------|------|---------|-------------|
| POST | \`/decompose\` | \`decompose_upload()\` | Upload CSV, decompose aggregate load into sessions (simple or simulation method) |
| POST | \`/decompose-simulation\` | \`decompose_simulation()\` | Upload CSV, decompose using simulation-based optimization |
| GET | \`/optimizer-config\` | \`get_optimizer_config()\` | Get default optimizer config parameters |
| POST | \`/generate-annual\` | \`generate_annual()\` | Generate 8760 annual load from decomposition results |
| POST | \`/generate-annual-simple\` | \`generate_annual_simple()\` | Generate annual load from weekly pattern |
| POST | \`/save-to-project\` | \`save_depot_to_project()\` | Save decomposition result to depot project |
| POST | \`/save-annual-load\` | \`save_annual_load()\` | Save generated annual load to depot project |

### Vehicle Admin Routes (\`/api/v1/ev/vehicles\`)

| Method | Path | Handler | Description |
|--------|------|---------|-------------|
| GET | \`/\` | \`list_vehicles()\` | List all vehicles (any authenticated user) |
| GET | \`/<vehicle_id>\` | \`get_vehicle()\` | Get vehicle by ID |
| POST | \`/\` | \`create_vehicle()\` | Create vehicle (admin only) |
| PUT | \`/<vehicle_id>\` | \`update_vehicle()\` | Update vehicle (admin only) |
| DELETE | \`/<vehicle_id>\` | \`delete_vehicle()\` | Delete vehicle (admin only) |

## Frontend Components

### Page Structure

The entry point is \`FluxEVApp\` (\`apps/flux-ev-web/src/pages/FluxEVApp.jsx\`), which
provides routing:

- \`/ev\` -- \`EVProjectDashboard\` (project list)
- \`/ev/:projectId\` -- Project view (fleet or depot)
- \`/ev/:projectId/:section\` -- Section-specific view

\`FluxEV\` (\`apps/flux-ev-web/src/pages/FluxEV.jsx\`) is the embedded version used
when Flux EV is rendered inside the main Site DNA shell (no router; uses
\`RetroContext\` for section navigation).

Fleet projects render \`ScenariosSection\` directly (scenarios are the entry point).
Depot projects navigate between \`DepotUploadSection\` and \`DepotResultsSection\`.

### Feature Module (\`apps/flux-ev-web/src/features/ev/\`)

Exports hooks and composed section components:

| Export | Type | Description |
|--------|------|-------------|
| \`useEVProject\` | Hook | Project CRUD, state management, site capacity calculation |
| \`useEVScenarios\` | Hook | Scenario CRUD, simulation, auto-save, SOC trajectory retrieval |
| \`useEVSimulation\` | Hook | Legacy single-strategy simulation |
| \`useEVOptimization\` | Hook | LP optimization trigger |
| \`useVehicleDatabase\` | Hook | Vehicle database queries |
| \`EVProjectDashboard\` | Component | Project list with create/delete/rename |
| \`EVProjectHeader\` | Component | Project title bar |
| \`DepotUploadSection\` | Component | CSV upload for depot projects |
| \`DepotResultsSection\` | Component | Depot decomposition results viewer |
| \`ScenariosSection\` | Component | Orchestrates \`ScenarioList\` + \`ScenarioEditor\` |

### EV Components (\`apps/flux-ev-web/src/components/ev/\`)

| Component | File | Description |
|-----------|------|-------------|
| \`FleetBuilder\` | \`FleetBuilder.jsx\` | Add/remove vehicles to fleet, vehicle type picker |
| \`VehicleCard\` | \`VehicleCard.jsx\` | Individual vehicle config (battery, miles, schedule) |
| \`VehicleView\` | \`VehicleView.jsx\` | Read-only vehicle detail display |
| \`ChargerConfig\` | \`ChargerConfig.jsx\` | Charger infrastructure configuration |
| \`ChargerCard\` | \`ChargerCard.jsx\` | Individual charger type card |
| \`ChargerView\` | \`ChargerView.jsx\` | Read-only charger detail display |
| \`SiteConfig\` | \`SiteConfig.jsx\` | Site electrical infrastructure (voltage, amperage, capacity) |
| \`TariffConfig\` | \`TariffConfig.jsx\` | Tariff selection via \`UnifiedTariffSelector\` |
| \`ScenarioList\` | \`ScenarioList.jsx\` | List of scenarios with simulate/duplicate/delete actions |
| \`ScenarioEditor\` | \`ScenarioEditor.jsx\` | Full scenario editing (fleet, chargers, tariff, strategy) |
| \`ScenarioDetailPanel\` | \`ScenarioDetailPanel.jsx\` | Scenario results detail view |
| \`ScenarioComparisonTable\` | \`ScenarioComparisonTable.jsx\` | Compare scenarios side-by-side |
| \`ScenarioComparisonRow\` | \`ScenarioComparisonRow.jsx\` | Single row in comparison table |
| \`EVLoadChart\` | \`EVLoadChart.jsx\` | Load profile chart (hourly or 15-min) |
| \`SOCTrajectoryChart\` | \`SOCTrajectoryChart.jsx\` | Per-vehicle SOC over time chart |
| \`OptimizationResults\` | \`OptimizationResults.jsx\` | Optimization output display (cost, peak, schedules) |
| \`EVResults\` | \`EVResults.jsx\` | Legacy results summary |
| \`AnnualLoadPreview\` | \`AnnualLoadPreview.jsx\` | Annual 8760 load profile preview chart |
| \`ExportDialog\` | \`ExportDialog.jsx\` | Export EV load to Site DNA project |
| \`ImportFluxEVDialog\` | \`ImportFluxEVDialog.jsx\` | Import EV load into Site DNA scenario |
| \`InfrastructureWarningBanner\` | \`InfrastructureWarningBanner.jsx\` | Warning when fleet exceeds charger capacity |
| \`DepotUploader\` | \`DepotUploader.jsx\` | CSV file upload for depot load decomposition |
| \`DepotSimulation\` | \`DepotSimulation.jsx\` | Depot simulation results visualization |
| \`DecompositionResults\` | \`DecompositionResults.jsx\` | Session decomposition output |
| \`DecompositionTabs\` | \`depot/DecompositionTabs.jsx\` | Tabbed view of decomposition data |
| \`SessionTimelineCanvas\` | \`depot/SessionTimelineCanvas.jsx\` | Canvas-rendered session timeline |
| \`SessionsTable\` | \`depot/SessionsTable.jsx\` | Table of individual charging sessions |
| \`MethodInfoPanel\` | \`depot/MethodInfoPanel.jsx\` | Decomposition method description |

## Optimizer

### LP Formulation

**File:** \`apps/flux-ev-api/services/ev_optimizer.py\`

**Solver:** HiGHS (via \`highspy\`), linear programming.

**Function:** \`optimize_charging_schedule(fleet, chargers, tariff, site_limits, resolution_minutes, n_hours, other_agents_power_kw, admm_target_kw, admm_rho, warm_start_primal)\`

#### Decision Variables

| Variable | Index | Count | Bounds | Description |
|----------|-------|-------|--------|-------------|
| \`charge[v,t]\` | \`v * n_intervals + t\` | \`n_vehicles * n_intervals\` | \`[0, max_charge_kw]\` if present, \`[0, 0]\` if absent | Charging power per vehicle per interval |
| \`soc[v,t]\` | \`soc_base_idx + v * n_intervals + t\` | \`n_vehicles * n_intervals\` | \`[0, 100]\` | SOC percentage per vehicle per interval |
| \`soc_slack[v]\` | \`soc_slack_base_idx + v\` | \`n_vehicles\` | \`[0, inf)\` | Soft slack for SOC departure target |
| \`peak[m]\` | \`peak_base_idx + m\` | 1-12 (monthly) | \`[0, inf)\` | Monthly peak demand variables |
| \`slack_site[t]\` | \`slack_base_idx + t\` | \`n_intervals\` (if site_limits) | \`[0, inf)\` | Soft slack for site power limits |

When ADMM is active, additional variables are appended:

| Variable | Index | Description |
|----------|-------|-------------|
| \`total_charging[t]\` | \`tc_base_idx + t\` | Auxiliary: aggregate charging power at timestep t |
| \`w[t]\` | \`w_base_idx + t\` | Epigraph variable for PWL approximation |

#### Objective Function

\`\`\`
minimize  sum_v sum_t [ (energy_rate[t] / intervals_per_hour + priority_adj + hold_adj) * charge[v,t] ]
        + sum_m [ demand_charge * peak[m] ]
        + sum_v [ soc_penalty * soc_slack[v] ]
        + sum_t [ slack_penalty * slack_site[t] ]     (if site_limits)
        + sum_t [ w[t] ]                               (if ADMM active)
\`\`\`

Where:

- \`energy_rate[t]\` = TOU energy rate for hour containing interval t ($/kWh)
- \`priority_adj\` = +/-0.005 $/kWh based on vehicle priority and position in charging window. High priority vehicles get a discount early in window (charge first), penalty late.
- \`hold_adj\` = 0.002 $/kWh penalty for charging in first 70% of window when \`soc_hold_target\` is set (delays final top-up for battery health).
- \`demand_charge\` = $/kW/month applied to each monthly peak variable.
- \`soc_penalty\` = \`max(max_energy_rate * 100, demand_penalty * 10, 10.0)\` -- dominates to strongly prefer meeting SOC target.
- \`slack_penalty\` = \`max(demand_charge * 100, 1000.0)\` -- prefer respecting site limits but finite so LP stays feasible.

#### Constraints

**Constraint 1: SOC continuity**
\`\`\`
soc[v, t+1] - soc[v, t] - charge[v, t] * (dt / battery_kwh * 100) = rhs
\`\`\`
Where \`rhs = -driving_loss_pct\` at departure intervals (vehicle leaves and drives), \`0\` otherwise.

\`driving_loss_pct = (daily_miles * efficiency_kwh_per_mile) / battery_kwh * 100\`

Initial condition: \`soc[v, 0] = initial_soc\` (fixed equality).

**Constraint 1b: SOC departure targets**

For each vehicle at each departure interval:
- Hard minimum: \`soc[v, departure] >= min_soc_floor\` (10% safety floor or \`min_soc_buffer\`)
- Soft target: \`soc[v, departure] + soc_slack[v] >= target_soc\`

**Constraint 2: Infrastructure capacity (HARD)**
\`\`\`
sum_v (charge[v,t] * quantity[v]) <= total_charger_kw    for all t
\`\`\`

**Constraint 2b: Site power limits (SOFT, coordination)**
\`\`\`
sum_v (charge[v,t] * quantity[v]) - slack_site[t] <= site_limits[hour]
\`\`\`
Only active when \`site_limits\` is provided (multi-agent negotiation).

**Constraint 3: Monthly peak demand tracking**
\`\`\`
peak[m] >= sum_v (charge[v,t] * quantity[v]) + other_agents_power[t]    for all t in month m
\`\`\`
When \`other_agents_power_kw\` is provided (best-response mode), the peak tracks the
system peak, so demand cost correctly reflects EV's contribution to the combined site peak.

**ADMM PWL constraints (when \`admm_rho > 0\` and \`admm_target_kw\` is provided):**

Linking: \`total_charging[t] = sum_v (charge[v,t] * quantity[v])\`

PWL tangent lines (K=8 per timestep):
\`\`\`
w[t] >= slope_k * total_charging[t] + intercept_k
\`\`\`
Where \`slope_k = rho * (x_k - target[t])\`, \`intercept_k = 0.5 * rho * (x_k - target[t])^2 - slope_k * x_k\`, and \`x_k\` samples uniformly across \`[0, total_charger_kw]\`.

This approximates the quadratic ADMM penalty \`(rho/2) * ||total_charging - target||^2\` using 8 tangent lines per timestep instead of a QP Hessian.

### MPC Controller

**File:** \`apps/flux-ev-api/services/ev_mpc_controller.py\`

**Class:** \`EVMPCController(horizon_hours=24, timestep_minutes=15)\`


---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
